import { Tooltip } from '@ntconsult/aluma-react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Allocation, AllocationStatus } from '../types/Allocation';
import { formatDisplayDate } from '../utils/data';
import { contractLabel } from '../utils/labels';
import { formatMoney } from '../utils/money';

/** Preenchimento por status: marca cheia só no vigente; futuro recua (tracejado), encerrado esmaece. */
const FILL_BY_STATUS: Record<AllocationStatus, string> = {
  active: 'bg-brand-primary text-brand-primary-foreground',
  upcoming: 'border border-brand-primary border-dashed bg-brand-primary-subtle text-foreground',
  closed: 'border border-border bg-muted text-foreground-muted',
};

const STATUS_LABEL_KEY: Record<
  AllocationStatus,
  'linhaDoTempo.legendaAtiva' | 'linhaDoTempo.legendaFutura' | 'linhaDoTempo.legendaEncerrada'
> = {
  active: 'linhaDoTempo.legendaAtiva',
  upcoming: 'linhaDoTempo.legendaFutura',
  closed: 'linhaDoTempo.legendaEncerrada',
};

type TimelineBarProps = {
  allocation: Allocation;
  leftPct: number;
  widthPct: number;
  top: number;
  height: number;
  /** px estimados da barra — define quanto de texto cabe (código+% / só % / nada). */
  barPx: number;
  onOpen: () => void;
};

/**
 * Uma alocação no Gantt: botão absoluto, focável (abre a dica no foco, para
 * teclado) e clicável (abre o detalhe só-leitura). Cor = status; borda esquerda
 * = faturável; borda direita = fim estimado (tracejado) ou "continua" (seta).
 */
export function TimelineBar({
  allocation,
  leftPct,
  widthPct,
  top,
  height,
  barPx,
  onOpen,
}: TimelineBarProps) {
  const { t } = useTranslation('alocacao');
  const estimatedEnd = allocation.endDate !== null && !allocation.fimConfirmado;
  const openEnd = allocation.endDate === null;
  const tier = barPx >= 64 ? 'full' : barPx >= 28 ? 'pct' : 'none';
  const projectName = `${allocation.project.axisCode} - ${allocation.project.name}`;

  return (
    <Tooltip content={<BarDetails allocation={allocation} />} side="top" width="lg">
      <button
        type="button"
        onClick={onOpen}
        aria-label={t('linhaDoTempo.barra', {
          projeto: projectName,
          percentual: allocation.allocatedPercentage,
        })}
        className={`absolute flex items-center rounded-sm text-left ${FILL_BY_STATUS[allocation.status]} cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-1`}
        style={{ left: `${leftPct}%`, width: `${widthPct}%`, top, height }}
      >
        {estimatedEnd && (
          <span
            aria-hidden="true"
            className="absolute inset-y-0.5 right-0 border-border-strong border-r-2 border-dashed"
          />
        )}
        {openEnd && (
          <ChevronRight
            aria-hidden="true"
            size={12}
            className="absolute inset-y-0 right-0 my-auto shrink-0"
          />
        )}
        {tier !== 'none' && (
          <span
            className={`min-w-0 flex-1 truncate ps-1.5 text-caption ${openEnd ? 'pe-4' : 'pe-1.5'}`}
          >
            {tier === 'full' && `${allocation.project.axisCode} · `}
            <span className="font-semibold">{allocation.allocatedPercentage}%</span>
          </span>
        )}
      </button>
    </Tooltip>
  );
}

/** Conteúdo da dica: texto puro (a dica tem fundo invertido `bg-foreground/text-surface`; badges/barras ficariam ilegíveis). */
function BarDetails({ allocation }: { allocation: Allocation }) {
  const { t, i18n } = useTranslation('alocacao');
  const locale = i18n.language;

  return (
    <div className="flex flex-col gap-0.5 text-start">
      <span className="font-semibold">
        {allocation.project.axisCode} - {allocation.project.name}
      </span>
      <span>{allocation.client.name}</span>
      <span>{contractLabel(allocation.contract)}</span>
      <span>
        {formatDisplayDate(allocation.startDate, locale)} –{' '}
        {allocation.endDate
          ? formatDisplayDate(allocation.endDate, locale)
          : t('tabela.semTermino')}
        {allocation.endDate && !allocation.fimConfirmado && ` (${t('tabela.fimEstimado')})`}
      </span>
      <span>
        {t(STATUS_LABEL_KEY[allocation.status])} · {allocation.allocatedPercentage}% ·{' '}
        {allocation.billable
          ? t('linhaDoTempo.legendaFaturavel')
          : t('linhaDoTempo.legendaNaoFaturavel')}
      </span>
      {allocation.salesProfile && (
        <span>
          {t('tabela.perfilVenda')}: {allocation.salesProfile.name}
        </span>
      )}
      {allocation.allocatedCost !== null && allocation.costCurrency && (
        <span>
          {t('tabela.custoAlocado')}:{' '}
          {formatMoney(allocation.allocatedCost, allocation.costCurrency.iso, locale)}
        </span>
      )}
      {allocation.monthlyGrossRevenue !== null && allocation.billingCurrency && (
        <span>
          {t('tabela.fatBrutoMes')}:{' '}
          {formatMoney(allocation.monthlyGrossRevenue, allocation.billingCurrency.iso, locale)}
        </span>
      )}
      {allocation.tags.length > 0 && (
        <span>{allocation.tags.map((tag) => tag.name).join(', ')}</span>
      )}
    </div>
  );
}
